/* mg-bundle-builder.js — MudGear Bundle Builder v2
 *
 * Architecture: collection template (collection.bundle-builder) instead of a
 * hand-rolled custom page with server-side grid + pre-rendered modals. This
 * file injects "Add to Box" buttons onto Impulse-rendered product cards, lazy-
 * fetches product data from /products/<handle>.js on first click, builds the
 * variant picker modal in JS, and hydrates/updates the sticky summary panel.
 *
 * Entrypoint: [data-mg-summary] section element (mg-bundle-builder-summary.liquid).
 * If that element is absent this script exits immediately — safe to load on any page.
 *
 * Spec: docs/specs/mg-bundle-builder.md
 */
(function () {
  'use strict';

  var summaryEl = document.querySelector('[data-mg-summary]');
  if (!summaryEl) return;

  // ─── Settings from section data attributes ──────────────────────────────────
  var settings = {
    tier1: { qty: parseInt(summaryEl.dataset.tier1MinQty, 10) || 2,  pct: parseInt(summaryEl.dataset.tier1DiscountPct, 10) || 15 },
    tier2: { qty: parseInt(summaryEl.dataset.tier2MinQty, 10) || 3,  pct: parseInt(summaryEl.dataset.tier2DiscountPct, 10) || 33 },
    tier3: { qty: parseInt(summaryEl.dataset.tier3MinQty, 10) || 5,  pct: parseInt(summaryEl.dataset.tier3DiscountPct, 10) || 40 },
    upsellTemplate: summaryEl.dataset.upsellTemplate || 'Add [n] more item(s) to get [pct]% off!',
    upsellProgress: summaryEl.dataset.upsellProgress || 'You now have [cur]% off — add [n] more item(s) to get [pct]% off!',
    upsellComplete: summaryEl.dataset.upsellComplete || "🎉 You've got the best discount!"
  };

  // Eligible product IDs for bundle summary.
  // Seeded server-side with the first 50 collection products (window.MgBundleEligibleIds),
  // then grows as the user browses paginated pages via the MutationObserver.
  var eligibleProductIds = new Set(
    (window.MgBundleEligibleIds || []).map(String)
  );

  var modalsContainer = null; // lazily created <div id="mg-bb-modals"> appended to <body>
  var openModalEl = null;
  var lastFocus = null;
  var modalCache = {};       // handle → modal element, so each product is only built once

  // Optimistic stepper state: lineKey -> target qty awaiting /cart/change.js.
  // Rapid +/− clicks coalesce into ONE request with the final quantity, and
  // the target is read from here (not the chip DOM, which lags the server).
  var pendingQty = {};
  var pendingTimers = {};
  function hasPendingLineOps() { return Object.keys(pendingQty).length > 0; }

  // The builder's own drawer refreshes echo back as 'mg:cart:changed' (the
  // drawer dispatches it from updateContents). Suppress that one echo so each
  // tracker action doesn't rebuild the chips twice — churn mid-click can
  // swallow the user's next click.
  var suppressNextCartChanged = false;
  function refreshDrawerQuietly() {
    if (window.MgCartDrawer && typeof window.MgCartDrawer.refresh === 'function') {
      suppressNextCartChanged = true;
      return Promise.resolve(window.MgCartDrawer.refresh());
    }
    return Promise.resolve();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // PRODUCT ID COLLECTION
  // ────────────────────────────────────────────────────────────────────────────

  function collectProductIds() {
    document.querySelectorAll('#CollectionAjaxContent .grid-product[data-product-id]').forEach(function (el) {
      var id = el.dataset.productId;
      if (id) eligibleProductIds.add(String(id));
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // BUTTON INJECTION
  // Appends an "Add to Box" <button> to each Impulse product card that doesn't
  // already have one. Cards live at .grid-product[data-product-handle] inside
  // #CollectionAjaxContent.
  // ────────────────────────────────────────────────────────────────────────────

  function injectButtons() {
    document.querySelectorAll('#CollectionAjaxContent .grid-product[data-product-handle]').forEach(function (card) {
      if (card.querySelector('[data-mg-add-btn]')) return;
      var handle = card.dataset.productHandle;
      if (!handle) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mg-add-to-box-btn';
      btn.dataset.mgAddBtn = '';
      btn.dataset.productHandle = handle;
      btn.dataset.productId = card.dataset.productId || '';
      btn.textContent = 'Add to Box';
      card.appendChild(btn);
    });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // "ADD TO BOX" CLICK — delegated on document (capture = false)
  // ────────────────────────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-mg-add-btn]');
    if (!btn) return;
    e.preventDefault();
    openOrFetchModal(btn.dataset.productHandle, btn);
  });

  // ────────────────────────────────────────────────────────────────────────────
  // LAZY MODAL FETCH + BUILD
  // ────────────────────────────────────────────────────────────────────────────

  function openOrFetchModal(handle, triggerBtn) {
    if (modalCache[handle]) {
      openModal(modalCache[handle], triggerBtn);
      return;
    }

    triggerBtn.disabled = true;
    triggerBtn.textContent = 'Loading…';

    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (product) {
        ensureModalsContainer();
        var modal = buildModal(product);
        modalsContainer.appendChild(modal);
        modalCache[handle] = modal;
        triggerBtn.disabled = false;
        refreshCardButtons(); // restores "Add to Box" / "✓ Added" as appropriate
        openModal(modal, triggerBtn);
      })
      .catch(function (err) {
        console.warn('[mg-bundle-builder] product fetch:', err);
        triggerBtn.disabled = false;
        refreshCardButtons();
      });
  }

  function ensureModalsContainer() {
    if (modalsContainer) return;
    modalsContainer = document.createElement('div');
    modalsContainer.id = 'mg-bb-modals';
    document.body.appendChild(modalsContainer);
    modalsContainer.addEventListener('click', handleModalClick);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL BUILDER — constructs modal DOM from /products/<handle>.js response
  // ────────────────────────────────────────────────────────────────────────────

  function buildModal(product) {
    var firstVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];

    var variantsData = product.variants.map(function (v) {
      return {
        id: v.id,
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || '',
        available: v.available,
        price: v.price,
        compare_at_price: v.compare_at_price || 0,
        featured_image: v.featured_image ? (v.featured_image.src || '') : ''
      };
    });

    var hasDefaultVariant = product.variants.length === 1 && product.variants[0].title === 'Default Title';
    // `/products/<handle>.js` returns featured_image and images as plain URL
    // strings (or null), NOT image objects. Variant featured_image IS an
    // object with .src — but we want the product-level here.
    var featuredImg = product.featured_image || (product.images && product.images[0]) || '';

    var optionsHtml = '';
    if (!hasDefaultVariant && product.options && product.options.length) {
      optionsHtml = '<div class="mg-bundle-builder__modal-options">';
      product.options.forEach(function (optName, optIdx) {
        // /products/<handle>.js can return options as array of strings OR
        // array of objects ({name, values, position}). Handle both.
        var optLabel = (optName && typeof optName === 'object') ? (optName.name || '') : optName;
        var vals = getOptionValues(product.variants, optIdx);
        optionsHtml += '<div class="mg-bundle-builder__modal-option" data-mg-modal-option-index="' + optIdx + '">';
        optionsHtml += '<div class="mg-bundle-builder__modal-option-label">' + esc(optLabel) + '</div>';
        optionsHtml += '<div class="mg-bundle-builder__modal-option-values">';
        vals.forEach(function (value) {
          var key = 'option' + (optIdx + 1);
          var isSelected = firstVariant[key] === value;
          var isAvail = product.variants.some(function (v) { return v[key] === value && v.available; });
          optionsHtml += '<button type="button"'
            + ' class="mg-bundle-builder__modal-option-value' + (isSelected ? ' mg-bundle-builder__modal-option-value--selected' : '') + '"'
            + ' data-mg-modal-option-value'
            + ' data-option-index="' + optIdx + '"'
            + ' data-option-value="' + escAttr(value) + '"'
            + ' aria-pressed="' + (isSelected ? 'true' : 'false') + '"'
            + (isAvail ? '' : ' disabled')
            + '>' + esc(value) + '</button>';
        });
        optionsHtml += '</div></div>';
      });
      optionsHtml += '</div>';
    }

    var compareAtHtml = (firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price)
      ? '<span class="mg-bundle-builder__modal-price-compare" data-mg-modal-price-compare>' + formatMoney(firstVariant.compare_at_price) + '</span>'
      : '';

    var modal = document.createElement('div');
    modal.className = 'mg-bundle-builder__modal';
    modal.setAttribute('data-mg-modal', '');
    modal.setAttribute('data-product-id', String(product.id));
    modal.setAttribute('data-variants', JSON.stringify(variantsData));
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'mg-modal-title-' + product.id);
    modal.hidden = true;

    modal.innerHTML = ''
      + '<div class="mg-bundle-builder__modal-backdrop" data-mg-modal-backdrop></div>'
      + '<div class="mg-bundle-builder__modal-panel">'
      +   '<button type="button" class="mg-bundle-builder__modal-close" data-mg-modal-close aria-label="Close">'
      +     '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      +       '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>'
      +     '</svg>'
      +   '</button>'
      +   '<div class="mg-bundle-builder__modal-body">'
      +     '<div class="mg-bundle-builder__modal-image-wrap">'
      +       (featuredImg ? '<img class="mg-bundle-builder__modal-image" data-mg-modal-image src="' + escAttr(featuredImg) + '" alt="' + escAttr(product.title) + '" loading="lazy">' : '')
      +     '</div>'
      +     '<div class="mg-bundle-builder__modal-info">'
      +       '<h2 class="mg-bundle-builder__modal-title" id="mg-modal-title-' + product.id + '">' + esc(product.title) + '</h2>'
      +       '<div class="mg-bundle-builder__modal-price" data-mg-modal-price>'
      +         compareAtHtml
      +         '<span class="mg-bundle-builder__modal-price-current" data-mg-modal-price-current>' + formatMoney(firstVariant.price) + '</span>'
      +       '</div>'
      +       optionsHtml
      +       '<div class="mg-bundle-builder__modal-qty">'
      +         '<span class="mg-bundle-builder__modal-qty-label">Quantity</span>'
      +         '<div class="mg-bundle-builder__modal-qty-stepper">'
      +           '<button type="button" class="mg-bundle-builder__modal-qty-btn" data-mg-modal-qty-decrement aria-label="Decrease quantity">−</button>'
      +           '<input type="number" class="mg-bundle-builder__modal-qty-input" data-mg-modal-qty-input min="1" value="1" inputmode="numeric" aria-label="Quantity">'
      +           '<button type="button" class="mg-bundle-builder__modal-qty-btn" data-mg-modal-qty-increment aria-label="Increase quantity">+</button>'
      +         '</div>'
      +       '</div>'
      +       '<button type="button"'
      +         ' class="mg-bundle-builder__modal-add"'
      +         ' data-mg-modal-add'
      +         ' data-current-variant-id="' + firstVariant.id + '"'
      +         (firstVariant.available ? '' : ' disabled aria-disabled="true"')
      +       '>'
      +         '<span class="mg-bundle-builder__modal-add-label" data-mg-modal-add-label>' + (firstVariant.available ? 'Add to Box' : 'Sold out') + '</span>'
      +       '</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    return modal;
  }

  function getOptionValues(variants, optIdx) {
    var seen = new Set();
    var vals = [];
    variants.forEach(function (v) {
      var val = v['option' + (optIdx + 1)];
      if (val && !seen.has(val)) { seen.add(val); vals.push(val); }
    });
    return vals;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL EVENT HANDLER (delegated on #mg-bb-modals)
  // ────────────────────────────────────────────────────────────────────────────

  function handleModalClick(e) {
    // Close
    if (e.target.closest('[data-mg-modal-close]') || e.target.matches('[data-mg-modal-backdrop]')) {
      closeModalIfOpen();
      return;
    }

    // Option value selection
    var optBtn = e.target.closest('[data-mg-modal-option-value]');
    if (optBtn) {
      var row = optBtn.closest('.mg-bundle-builder__modal-option-values');
      if (row) {
        row.querySelectorAll('[data-mg-modal-option-value]').forEach(function (b) {
          b.classList.remove('mg-bundle-builder__modal-option-value--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        optBtn.classList.add('mg-bundle-builder__modal-option-value--selected');
        optBtn.setAttribute('aria-pressed', 'true');
      }
      updateModalVariantState(optBtn.closest('[data-mg-modal]'));
      return;
    }

    // Qty stepper
    var dec = e.target.closest('[data-mg-modal-qty-decrement]');
    var inc = e.target.closest('[data-mg-modal-qty-increment]');
    if (dec || inc) {
      var modal = (dec || inc).closest('[data-mg-modal]');
      var input = modal.querySelector('[data-mg-modal-qty-input]');
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        if (dec) val = Math.max(1, val - 1);
        if (inc) val++;
        input.value = val;
      }
      return;
    }

    // Add to Box
    var addBtn = e.target.closest('[data-mg-modal-add]');
    if (!addBtn || addBtn.disabled) return;

    var addModal = addBtn.closest('[data-mg-modal]');
    var variantId = addBtn.dataset.currentVariantId;
    var qtyInput = addModal.querySelector('[data-mg-modal-qty-input]');
    var qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;
    if (!variantId) return;

    addBtn.disabled = true;
    var label = addModal.querySelector('[data-mg-modal-add-label]');
    var prevLabel = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add.js failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderSummary(cart);
        closeModalIfOpen();
        refreshDrawerQuietly();
        addBtn.disabled = false;
      })
      .catch(function (err) {
        console.warn('[mg-bundle-builder] add to cart failed:', err);
        if (label) label.textContent = prevLabel || 'Add to Box';
        addBtn.disabled = false;
      });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MODAL OPEN / CLOSE
  // ────────────────────────────────────────────────────────────────────────────

  function openModal(modal, trigger) {
    closeModalIfOpen();
    lastFocus = trigger || document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    openModalEl = modal;
    var qtyInput = modal.querySelector('[data-mg-modal-qty-input]');
    if (qtyInput) qtyInput.value = 1;
    updateModalVariantState(modal);
    var closeBtn = modal.querySelector('[data-mg-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModalIfOpen() {
    if (!openModalEl) return;
    openModalEl.hidden = true;
    document.body.style.overflow = '';
    openModalEl = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openModalEl) closeModalIfOpen();
  });

  // ────────────────────────────────────────────────────────────────────────────
  // VARIANT STATE — updates price, availability, add-button after option change
  // ────────────────────────────────────────────────────────────────────────────

  function updateModalVariantState(modal) {
    if (!modal) return;
    var variants;
    try { variants = JSON.parse(modal.dataset.variants || '[]'); } catch (e) { variants = []; }

    var selected = [];
    modal.querySelectorAll('[data-mg-modal-option-value].mg-bundle-builder__modal-option-value--selected').forEach(function (b) {
      var idx = parseInt(b.dataset.optionIndex, 10);
      if (!isNaN(idx)) selected[idx] = b.dataset.optionValue;
    });

    var matched = variants.find(function (v) {
      if (selected[0] !== undefined && v.option1 !== selected[0]) return false;
      if (selected[1] !== undefined && v.option2 !== selected[1]) return false;
      if (selected[2] !== undefined && v.option3 !== selected[2]) return false;
      return true;
    });

    var addBtn   = modal.querySelector('[data-mg-modal-add]');
    var addLabel = modal.querySelector('[data-mg-modal-add-label]');
    var priceCur = modal.querySelector('[data-mg-modal-price-current]');
    var priceCmp = modal.querySelector('[data-mg-modal-price-compare]');

    if (matched) {
      addBtn.dataset.currentVariantId = matched.id;
      if (matched.available) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        if (addLabel) addLabel.textContent = 'Add to Box';
      } else {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        if (addLabel) addLabel.textContent = 'Sold out';
      }
      if (priceCur) priceCur.textContent = formatMoney(matched.price);
      if (priceCmp) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          priceCmp.textContent = formatMoney(matched.compare_at_price);
          priceCmp.style.display = '';
        } else {
          priceCmp.style.display = 'none';
        }
      }
    } else if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      if (addLabel) addLabel.textContent = 'Unavailable';
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // SUMMARY PANEL
  // ────────────────────────────────────────────────────────────────────────────

  var inCartProductIds = new Set(); // eligible product ids currently in cart (for "✓ Added" card state)

  function refreshCardButtons() {
    document.querySelectorAll('[data-mg-add-btn]').forEach(function (btn) {
      var added = inCartProductIds.has(String(btn.dataset.productId));
      btn.classList.toggle('mg-add-to-box-btn--added', added);
      btn.textContent = added ? '✓ Added' : 'Add to Box';
    });
  }

  function renderSummary(cart) {
    if (!cart || !cart.items) return;
    // Don't clobber optimistic stepper UI while clicks are still pending.
    if (hasPendingLineOps()) return;
    var items = cart.items.filter(function (i) { return eligibleProductIds.has(String(i.product_id)); });
    var totalQty = items.reduce(function (s, i) { return s + i.quantity; }, 0);

    inCartProductIds = new Set(items.map(function (i) { return String(i.product_id); }));
    refreshCardButtons();

    if (totalQty === 0) {
      summaryEl.hidden = true;
      document.body.style.paddingBottom = '';
      return;
    }

    summaryEl.hidden = false;
    // Reserve space so the last grid row isn't hidden behind the sticky panel
    document.body.style.paddingBottom = (summaryEl.offsetHeight || 120) + 'px';

    var upsellEl = summaryEl.querySelector('[data-mg-summary-upsell]');
    if (upsellEl) upsellEl.textContent = computeUpsellMessage(totalQty);

    var itemsContainer = summaryEl.querySelector('[data-mg-summary-items]');
    var tpl = summaryEl.querySelector('[data-mg-summary-chip-template]');
    if (itemsContainer && tpl) {
      // PATCH IN PLACE — never wholesale-rebuild the chip row. Rebuilding
      // destroys the +/− buttons mid-interaction, and a click that lands
      // during the rebuild is silently swallowed.
      var existing = {};
      Array.from(itemsContainer.children).forEach(function (ch) {
        existing[ch.dataset.lineKey] = ch;
      });
      var seen = {};
      items.forEach(function (item) {
        seen[item.key] = true;
        var chip = existing[item.key];
        if (!chip) {
          chip = tpl.content.firstElementChild.cloneNode(true);
          chip.dataset.lineKey = item.key;
          itemsContainer.appendChild(chip);
        }
        chip.style.display = '';
        var img = chip.querySelector('[data-mg-chip-image]');
        if (img && img.src !== (item.image || '')) { img.src = item.image || ''; img.alt = item.product_title || ''; }
        var titleEl = chip.querySelector('[data-mg-chip-title]');
        if (titleEl) titleEl.textContent = item.product_title || '';
        var variantEl = chip.querySelector('[data-mg-chip-variant]');
        if (variantEl) variantEl.textContent = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
        var priceEl = chip.querySelector('[data-mg-chip-price]');
        if (priceEl) priceEl.textContent = formatMoney(item.final_line_price || (item.final_price * item.quantity));
        var qtyEl = chip.querySelector('[data-mg-chip-qty]');
        if (qtyEl) qtyEl.textContent = item.quantity;
      });
      Object.keys(existing).forEach(function (k) {
        if (!seen[k]) existing[k].remove();
      });
    }

    var subtotal = items.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    var total    = items.reduce(function (s, i) { return s + i.final_line_price; }, 0);
    var discount = subtotal - total;

    var countEl = summaryEl.querySelector('[data-mg-summary-count]');
    if (countEl) countEl.textContent = totalQty + (totalQty === 1 ? ' item' : ' items') + ' in box';
    var subtotalEl = summaryEl.querySelector('[data-mg-summary-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
    var discRow = summaryEl.querySelector('[data-mg-summary-discount-row]');
    var discEl  = summaryEl.querySelector('[data-mg-summary-discount]');
    if (discount > 0) {
      if (discRow) discRow.hidden = false;
      if (discEl) discEl.textContent = '−' + formatMoney(discount);
    } else {
      if (discRow) discRow.hidden = true;
    }
    var totalEl = summaryEl.querySelector('[data-mg-summary-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);
  }

  function computeUpsellMessage(qty) {
    // F3-style two-part messaging, driven by configurable section settings:
    //   no discount yet  → upsellTemplate         ("Add [n] more … [pct]% off")
    //   tier active+next → upsellProgress         ("You now have [cur]% off — add [n] more … [pct]% off")
    //   top tier reached → upsellComplete
    var tiers = [settings.tier1, settings.tier2, settings.tier3]
      .filter(function (t) { return t.qty > 0 && t.pct > 0; })
      .sort(function (a, b) { return a.qty - b.qty; });

    var current = null, next = null;
    tiers.forEach(function (t) {
      if (qty >= t.qty) current = t;
      else if (!next) next = t;
    });

    if (current && !next) return settings.upsellComplete;
    if (current && next)  return tmpl(settings.upsellProgress, next.qty - qty, next.pct, current.pct);
    if (next)             return tmpl(settings.upsellTemplate, next.qty - qty, next.pct);
    return '';
  }

  function tmpl(template, n, pct, cur) {
    return template
      .replace(/\[n\]/g, String(n))
      .replace(/\[pct\]/g, String(pct))
      .replace(/\[cur\]/g, String(cur !== undefined ? cur : ''));
  }

  // Chip controls + Review Box button (delegated on summary element)
  summaryEl.addEventListener('click', function (e) {
    if (e.target.closest('[data-mg-summary-review]')) {
      if (window.MgCartDrawer && typeof window.MgCartDrawer.open === 'function') {
        window.MgCartDrawer.open();
      } else {
        var cartIcon = document.querySelector('.js-drawer-open-cart, [data-mg-cart-open]');
        if (cartIcon) cartIcon.click();
      }
      return;
    }

    var chip = e.target.closest('[data-mg-summary-chip]');
    if (!chip) return;
    var lineKey = chip.dataset.lineKey;
    if (!lineKey) return;

    var qtySpan = chip.querySelector('[data-mg-chip-qty]');
    var currentQty = parseInt(qtySpan ? qtySpan.textContent : '1', 10) || 1;
    var delta = null;
    if (e.target.closest('[data-mg-chip-decrement]')) delta = -1;
    else if (e.target.closest('[data-mg-chip-increment]')) delta = 1;
    else if (e.target.closest('[data-mg-chip-remove]')) delta = 'remove';

    if (delta === null) return;
    queueLineChange(chip, lineKey, currentQty, delta);
  });

  // Coalesce rapid stepper clicks: update the chip text immediately, then send
  // ONE /cart/change.js with the final quantity after a short quiet period.
  function queueLineChange(chip, lineKey, domQty, delta) {
    var base = (pendingQty[lineKey] !== undefined) ? pendingQty[lineKey] : domQty;
    var target = (delta === 'remove') ? 0 : Math.max(0, base + delta);
    pendingQty[lineKey] = target;

    var qtySpan = chip.querySelector('[data-mg-chip-qty]');
    if (qtySpan) qtySpan.textContent = target;
    if (target === 0) chip.style.display = 'none';

    clearTimeout(pendingTimers[lineKey]);
    pendingTimers[lineKey] = setTimeout(function () {
      var qty = pendingQty[lineKey];
      delete pendingQty[lineKey];
      delete pendingTimers[lineKey];
      commitLineChange(lineKey, qty);
    }, delta === 'remove' ? 0 : 350);
  }

  function commitLineChange(lineKey, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderSummary(cart);
        refreshDrawerQuietly();
      })
      .catch(function (err) { console.warn('[mg-bundle-builder] cart change failed:', err); });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MONEY FORMATTER
  // ────────────────────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // STRING ESCAPING (for JS-built modal HTML)
  // ────────────────────────────────────────────────────────────────────────────

  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MUTATIONOBSERVER — re-inject buttons when Impulse updates the grid
  // (filter apply, pagination, infinite scroll)
  // ────────────────────────────────────────────────────────────────────────────

  function setupObserver() {
    var ajaxContent = document.getElementById('CollectionAjaxContent');
    if (!ajaxContent) return;

    var debounceTimer;
    new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        collectProductIds();
        injectButtons();
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(renderSummary)
          .catch(function () {});
      }, 120);
    }).observe(ajaxContent, { childList: true, subtree: true });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // CART → TRACKER SYNC
  // The cart drawer dispatches 'mg:cart:changed' after every cart mutation it
  // performs (qty change, remove, upsell add, rewards add/remove). Re-render
  // the tracker from the real cart so it never goes stale. Debounced to absorb
  // rapid successive mutations.
  // ────────────────────────────────────────────────────────────────────────────

  var cartChangedTimer;
  document.addEventListener('mg:cart:changed', function () {
    // Skip the echo from our own drawer refreshes (see refreshDrawerQuietly).
    if (suppressNextCartChanged) { suppressNextCartChanged = false; return; }
    clearTimeout(cartChangedTimer);
    cartChangedTimer = setTimeout(function () {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(renderSummary)
        .catch(function () {});
    }, 100);
  });

  // ────────────────────────────────────────────────────────────────────────────
  // INIT
  // ────────────────────────────────────────────────────────────────────────────

  collectProductIds();
  injectButtons();
  fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(renderSummary)
    .catch(function (err) { console.warn('[mg-bundle-builder] initial cart fetch failed:', err); });
  setupObserver();

})();
